import { compact, difference, uniq } from 'lodash';
import * as React from 'react';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { updateBannersOrganizationIds } from '../../actions/banners-edit/organization';
import {
    NoticeType,
    resetGridData,
    showNoticeMessage
} from '../../actions/grid-screen';
import {
    BULK_UPDATE_ORGANIZATIONS,
    BulkUpdateOrganizationsAction,
    closeOrganizationsBulkEditor,
    INIT_ORGANIZATIONS_EDITORS,
    InitOrganizationsEditors,
    LOAD_ORGANIZATION_EDITOR_DATA,
    loadOrganizationEditorData,
    LoadOrganizationEditorData,
    RESET_ORGANIZATIONS_EDITOR,
    ResetOrganizationsEditor,
    SAVE_ORGANIZATION_TO_EDITOR,
    SaveOrganizationToEditor,
    updateOrganizationsEditorsData,
    updateOrganizationsEditorsDataStatus
} from '../../actions/organizationsEditor';
import { ShowUnsavedBannersButton } from '../../components/ShowUnsavedBannersButton/ShowUnsavedBannersButton';
import {
    mapEditorKeysToOrganisations,
    mapOrganizationDataFromApiFormat
} from '../../mappers/organization/organization';
import { selectOrganizationEditableIds } from '../../selectors/bannerEdit/massActions/massActions';
import { getBannersOrganizationSavedResultMessage } from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { BannerId } from '../../typings/banners';
import { MassActionId } from '../../typings/bannersEditState';
import { DnaEpic } from '../../typings/epic';
import {
    GdUpdateOrganizationInput,
    OrganizationsQuery
} from '../../typings/graphql-grid';
import {
    OrganizationData,
    OrganizationEditorParams,
    OrganizationsEditorDataStatus
} from '../../typings/organizations';
import { State } from '../../typings/state';

export const loadOrganizationsForEditEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(INIT_ORGANIZATIONS_EDITORS),
        mergeMap(
            (action: InitOrganizationsEditors): Observable<Action> => {
                const { login } = store$.value.commonData;
                const { params } = action;
                const orgIds: string[] = uniq(
                    compact(params.map(p => p.organizationId))
                );

                const editorsWithOrganization: OrganizationEditorParams[] = params.filter(
                    p => p.organizationId
                );

                if (!orgIds.length) {
                    return Observable.empty();
                }

                return from(
                    deps.organizationDataProvider.getOrganizations(
                        login,
                        orgIds
                    )
                ).pipe(
                    mergeMap(
                        (data: OrganizationsQuery): Observable<Action> => {
                            const organizations:
                                | Record<string, OrganizationData>
                                | undefined =
                                data.organizations &&
                                mapOrganizationDataFromApiFormat(data);

                            const editorsOrganizations: Record<
                                string,
                                OrganizationData
                            > = mapEditorKeysToOrganisations(
                                editorsWithOrganization,
                                organizations
                            );

                            return of(
                                updateOrganizationsEditorsData(
                                    editorsOrganizations
                                )
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            const editorsStates = editorsWithOrganization.reduce(
                                (acc, editorParams) => {
                                    const { editorKey } = editorParams;
                                    return {
                                        ...acc,
                                        [editorKey]:
                                            OrganizationsEditorDataStatus.ERROR
                                    };
                                },
                                {}
                            );

                            return of(
                                updateOrganizationsEditorsDataStatus(
                                    editorsStates
                                )
                            );
                        }
                    )
                );
            }
        )
    );
};

export const loadOrganizationForEditEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(LOAD_ORGANIZATION_EDITOR_DATA),
        mergeMap(
            (action: LoadOrganizationEditorData): Observable<Action> => {
                const { login } = store$.value.commonData;
                const { editorParams } = action;
                const { editorKey, organizationId } = editorParams;

                if (!organizationId) {
                    return Observable.empty();
                }

                return from(
                    deps.organizationDataProvider.getOrganizations(login, [
                        organizationId
                    ])
                ).pipe(
                    mergeMap(
                        (data: OrganizationsQuery): Observable<Action> => {
                            const organizations:
                                | Record<string, OrganizationData>
                                | undefined =
                                data.organizations &&
                                mapOrganizationDataFromApiFormat(data);

                            const editorsOrganizations: Record<
                                string,
                                OrganizationData
                            > = mapEditorKeysToOrganisations(
                                [editorParams],
                                organizations
                            );

                            return of(
                                updateOrganizationsEditorsData(
                                    editorsOrganizations
                                )
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> => {
                            return of(
                                updateOrganizationsEditorsDataStatus({
                                    [editorKey]:
                                        OrganizationsEditorDataStatus.ERROR
                                })
                            );
                        }
                    )
                );
            }
        )
    );
};

export const updateOrganizationInBanner: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_ORGANIZATION_TO_EDITOR),
        map((action: SaveOrganizationToEditor) => {
            return updateBannersOrganizationIds([action.editorParams]);
        })
    );
};

export const resetOrganizationsEditorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(RESET_ORGANIZATIONS_EDITOR),
        map((action: ResetOrganizationsEditor) => {
            const { editorKey, organizationId } = action.editorParams;
            const organization: OrganizationData | undefined = organizationId
                ? store$.value.organizationsData &&
                  store$.value.organizationsData[organizationId]
                : undefined;

            // случай, когда организация была не выбрана
            if (!organizationId) {
                return updateOrganizationsEditorsDataStatus({
                    [editorKey]: OrganizationsEditorDataStatus.EMPTY
                });
            }

            // случай, когда организация была выбрана и данные о ней были загружены без ошибок
            if (organization) {
                return updateOrganizationsEditorsDataStatus({
                    [editorKey]: OrganizationsEditorDataStatus.LOADED
                });
            }

            // В случае, когда есть ID, но нет данных
            // (например при смене ненайденной организации на новую)
            // делаем дополнительный запрос, чтобы компонент получил правильный статус данных
            return loadOrganizationEditorData({
                editorKey,
                organizationId
            });
        })
    );
};

export const bulkUpdateOrganizationsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(BULK_UPDATE_ORGANIZATIONS),
        mergeMap(
            (action: BulkUpdateOrganizationsAction): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;
                const selectedBanners = selectOrganizationEditableIds(state);
                const inputData = selectedBanners.map(
                    (id: BannerId): GdUpdateOrganizationInput => ({
                        bannerId: id,
                        permalinkId: action.organizationId || null
                    })
                );

                return from(
                    deps.organizationMutationExecutor.bulkUpdateOrganizations(
                        inputData,
                        login
                    )
                ).pipe(
                    mergeMap(result => {
                        const { updateOrganizations } = result;

                        const updatedBanners = updateOrganizations.updatedAds;
                        const updatedBannersCount = updatedBanners.length;
                        const selectedBannersCount = selectedBanners.length;
                        const unsavedBannersCount =
                            selectedBannersCount - updatedBannersCount;
                        const resultMessage = getBannersOrganizationSavedResultMessage(
                            updatedBannersCount,
                            unsavedBannersCount
                        );

                        if (!updatedBannersCount) {
                            showNoticeMessage(NoticeType.error, resultMessage);
                        } else if (
                            !unsavedBannersCount ||
                            unsavedBannersCount > 100
                        ) {
                            showNoticeMessage(
                                NoticeType.success,
                                resultMessage
                            );
                        } else {
                            const updatedBannersList = updatedBanners.map(
                                banner => {
                                    return banner ? banner.id.toString() : '';
                                }
                            );
                            const unsavedBanners = difference(
                                selectedBanners,
                                updatedBannersList
                            );

                            showNoticeMessage(
                                NoticeType.success,
                                resultMessage,
                                {
                                    customFields: {
                                        button: React.createElement(
                                            ShowUnsavedBannersButton,
                                            {
                                                pathname: state.router.pathname,
                                                login: state.commonData.login,
                                                bannersIds: unsavedBanners
                                            }
                                        )
                                    }
                                }
                            );
                        }

                        return [
                            resetGridData(),
                            closeOrganizationsBulkEditor(
                                MassActionId.bannerGrid
                            )
                        ];
                    })
                );
            }
        )
    );
};
